import { testId } from '../../utils';
import { Typography, Iconography } from '../primitives';
import { IconographyVariant } from '../primitives/Iconography';
import * as NavigationMenu from '@radix-ui/react-navigation-menu';
import { useTranslation } from '@rpi/i18n';
import clsx from 'clsx';
import React from 'react';
import { Link, LinkProps, To, useMatches } from 'react-router-dom';

export type SidebarItem = {
  title: string;
  path: string;
  iconKey?: IconographyVariant;
  children?: undefined;
  pathPrefix?: undefined;
};

export type SidebarCategory = {
  title: string;
  iconKey?: IconographyVariant;
  pathPrefix: string;
  children: SidebarItem[];
  path?: undefined;
};

type SidebarItemProps = SidebarItem & {
  isChild?: boolean;
};

type SidebarCategoryProps = SidebarCategory & {
  children: SidebarItemProps[];
};

const SidebarLink: React.FC<
  Omit<LinkProps, 'to'> & { to?: To; isActive: boolean }
> = ({ to, isActive, ...props }) => {
  const preventClose = React.useCallback((event: Event) => {
    event.preventDefault();
  }, []);
  if (!to) return <>{props.children}</>;

  return (
    <NavigationMenu.Link asChild active={isActive} onSelect={preventClose}>
      <Link to={to} {...props} />
    </NavigationMenu.Link>
  );
};

export const SidebarCategoryItem: React.FC<SidebarCategoryProps> = ({
  title,
  children,
  iconKey,
  pathPrefix,
}) => {
  const { t } = useTranslation();

  const preventClose = React.useCallback(
    (
      event:
        | React.PointerEvent<HTMLButtonElement>
        | React.PointerEvent<HTMLDivElement>
        | CustomEvent,
    ) => {
      event.preventDefault();
    },
    [],
  );

  return (
    <NavigationMenu.Item value={pathPrefix} {...testId('sidebar', 'category')}>
      <NavigationMenu.Trigger
        {...testId('sidebar', 'category', 'trigger')}
        title={t(`route.user.navigation.${title}`)!}
        className="h-12 w-full px-2 flex items-center group justify-between rounded hover:bg-blue-hover"
        onPointerMove={preventClose}
        onPointerLeave={preventClose}
      >
        <div className="flex gap-1">
          {iconKey && <Iconography style="rounded" variant={iconKey} />}
          <Typography
            variant="body-semibold"
            className="group-data-[state=closed]:text-gray-darkest"
          >
            {t(`route.user.navigation.${title}`)}
          </Typography>
        </div>
        <Iconography
          variant="keyboard_arrow_down"
          style="rounded"
          className="min-h-6 min-w-6 group-data-[state=closed]:-rotate-180 group-data-[state=closed]:text-gray-soft"
          aria-hidden
        />
      </NavigationMenu.Trigger>
      <NavigationMenu.Content
        onPointerEnter={preventClose}
        onPointerLeave={preventClose}
        onInteractOutside={preventClose}
      >
        <NavigationMenu.Sub orientation="vertical">
          <NavigationMenu.List className="flex flex-col">
            {children.map((item, index) => (
              <SidebarLinkItem
                {...item}
                isChild
                path={`${pathPrefix ?? ''}${item.path}`}
                key={index}
              />
            ))}
          </NavigationMenu.List>
        </NavigationMenu.Sub>
      </NavigationMenu.Content>
    </NavigationMenu.Item>
  );
};

export const SidebarLinkItem: React.FC<SidebarItemProps> = (props) => {
  const { t } = useTranslation();
  const matches = useMatches();

  const { title, iconKey, isChild, path } = props;
  const isActive = React.useMemo(() => {
    return matches.findIndex((match) => match.pathname === path) > -1;
  }, [matches, path]);

  return (
    <NavigationMenu.Item
      {...testId('sidebar', 'item')}
      className={clsx(
        'px-2 flex gap-1 items-center rounded hover:bg-blue-hover',
        {
          'bg-blue-selected': isActive,
          'pl-4': isChild,
        },
      )}
    >
      {iconKey && <Iconography style="rounded" variant={iconKey} />}
      <SidebarLink
        title={t(`route.user.navigation.${title}`)!}
        to={path}
        className={clsx('w-full no-underline h-12 flex items-center relative', {
          'border-l border-gray pl-2': isChild,
        })}
        isActive={isActive}
      >
        {isActive && isChild && (
          <div className="bg-blue-dark w-[3px] h-2/3 relative -left-[10px]" />
        )}
        <Typography
          variant="body-semibold"
          className={clsx(' hover:text-gray-soft', {
            'text-gray-soft': isActive,
            'text-gray-darkest': !isActive,
          })}
        >
          {t(`route.user.navigation.${title}`)}
        </Typography>
      </SidebarLink>
    </NavigationMenu.Item>
  );
};
